import { memo, useState, useRef, useCallback } from "react";
import styled from "styled-components";
import Tippy from '@tippyjs/react/headless';
import S2MosaicFinder from "./finders/S2MosaicFinder/S2MosaicFinder";
import { Button, Space, Drawer, Select, Popconfirm, Modal, Tag } from "antd";
import { PictureOutlined } from "@ant-design/icons";
import { useAtom } from 'jotai';
import { showSatelliteImagePanelAtom } from "~/components/Map/stores/mapStore";
import { s2mosaicItemsLEFTAtom, s2mosaicItemsRIGHTAtom } from "./finders/S2MosaicFinder/store";
import { s2MosaicQuarterlyItemsLEFTAtom, s2MosaicQuarterlyItemsRIGHTAtom } from "./finders/S2MosaicQuarterlyFinder/store";
import { landsatMosaicItemsLEFTAtom, landsatMosaicItemsRIGHTAtom } from "./finders/LandsatMosaicFinder/store";
import { s2singleItemsLEFTAtom, s2singleItemsRIGHTAtom } from "./finders/S2SingleFinder/store";
import { planetItemsLEFTAtom, planetItemsRIGHTAtom } from "./finders/PlanetFinder/store";
import { landsatItemsLEFTAtom, landsatItemsRIGHTAtom } from "./finders/LandsatFinder/store";
import dayjs from "dayjs";
import { UI_GroupHeader } from "../../PanelStyle";
import { RemoteImageAlert_GetList_ResponseItem, PlanetMosaicItem, S2MosaicQuarterly_Item, LandsatMosaic_Item } from "~/common/typescript/apis/RemoteImageAlertAPIs";
import { SatelliteTypeEnum } from "./store";
import { PiMagnifyingGlass, PiInfo, PiTrash, PiEye, PiEyeSlash, PiDownloadSimple } from 'react-icons/pi';
import { IoEllipsisHorizontal } from "react-icons/io5";
import { API_URL_ADMIN } from "~/config/config";

const SatelliteTab = () => {
  const [showS2MosaicPanel, setShowS2MosaicPanel] = useAtom(showSatelliteImagePanelAtom);
  const [s2mosaicItemsLeft, setS2MosaicItemsLeft] = useAtom(s2mosaicItemsLEFTAtom);
  const [s2mosaicItemsRight, setS2MosaicItemsRight] = useAtom(s2mosaicItemsRIGHTAtom);
  const [s2MosaicQuarterlyItemsLeft, setS2MosaicQuarterlyItemsLeft] = useAtom(s2MosaicQuarterlyItemsLEFTAtom);
  const [s2MosaicQuarterlyItemsRight, setS2MosaicQuarterlyItemsRight] = useAtom(s2MosaicQuarterlyItemsRIGHTAtom);
  const [landsatMosaicItemsLeft, setLandsatMosaicItemsLeft] = useAtom(landsatMosaicItemsLEFTAtom);
  const [landsatMosaicItemsRight, setLandsatMosaicItemsRight] = useAtom(landsatMosaicItemsRIGHTAtom);
  const [s2singleItemsLeft, setS2SingleItemsLeft] = useAtom(s2singleItemsLEFTAtom);
  const [s2singleItemsRight, setS2SingleItemsRight] = useAtom(s2singleItemsRIGHTAtom);
  const [planetItemsLeft, setPlanetItemsLeft] = useAtom(planetItemsLEFTAtom);
  const [planetItemsRight, setPlanetItemsRight] = useAtom(planetItemsRIGHTAtom);
  const [landsatItemsLeft, setLandsatItemsLeft] = useAtom(landsatItemsLEFTAtom);
  const [landsatItemsRight, setLandsatItemsRight] = useAtom(landsatItemsRIGHTAtom);

  // Calculate record counts
  const leftCount =
    s2mosaicItemsLeft.length +
    s2MosaicQuarterlyItemsLeft.length +
    landsatMosaicItemsLeft.length +
    s2singleItemsLeft.length +
    planetItemsLeft.length +
    landsatItemsLeft.length;

  const rightCount =
    s2mosaicItemsRight.length +
    s2MosaicQuarterlyItemsRight.length +
    landsatMosaicItemsRight.length +
    s2singleItemsRight.length +
    planetItemsRight.length +
    landsatItemsRight.length;

  const totalRecordCount = leftCount + rightCount;

  // State for the main filter drawer
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  // State for satellite type selection
  const [satelliteType, setSatelliteType] = useState<SatelliteTypeEnum>(SatelliteTypeEnum.S2_MOSAIC);
  // State for tracking visibility of each image (using image ID as key)
  const [imageVisibility, setImageVisibility] = useState<Record<string, boolean>>({});

  // Function to get name for S2Mosaic images
  const getS2MosaicName = (item: any) => {
    return `Ảnh ${dayjs(item.imageDescription['start_date']).format('DD/MM/YYYY')} - ${dayjs(item.imageDescription['end_date']).format('DD/MM/YYYY')}`;
  };

  // Function to get name for S2MosaicQuarterly images
  const getS2MosaicQuarterlyName = (item: S2MosaicQuarterly_Item) => {
    return item.title_vi || `Sentinel-2 Ghép Quý ${item.quarter} / ${item.year}`;
  };

  // Function to get name for LandsatMosaic images
  const getLandsatMosaicName = (item: LandsatMosaic_Item) => {
    return item.title_vi || `Landsat Kỳ ${item.period} / ${item.year}`;
  };

  // Function to get name for S2Single images
  const getS2SingleName = (item: RemoteImageAlert_GetList_ResponseItem) => {
    // Extract date from the ID in the format like "20241117T031011_105N_039E"
    const rawId = item.id;
    const id = rawId.split('/').at(-1);
    if (!id) return 'Ảnh không xác định';
    // Tách chuỗi đầu tiên (trước dấu "_")
    const datetimePart = id.split('_')[0]; // "20241117T031011"

    // Tách ngày
    const datePart = datetimePart.slice(0, 8); // "20241117"

    // Kết hợp lại thành định dạng ISO
    const isoString = `${datePart.slice(0, 4)}-${datePart.slice(4, 6)}-${datePart.slice(6, 8)}`;

    // Format the date
    const date = dayjs(isoString, 'YYYY-MM-DD');
    return `Ảnh ${dayjs(date).format('DD/MM/YYYY')}`;
  };

  // Function to get name for Planet images
  const getPlanetName = (item: PlanetMosaicItem) => {
    return `Ảnh ${item.title_vi || item.title} (${item.year})`;
  };

  // Function to get name for Landsat images
  const getLandsatName = (item: RemoteImageAlert_GetList_ResponseItem) => {
    const rawId = item.id;
    const shortId = rawId.split('_T').at(-1) || rawId.split('/').at(-1) || 'Unknown';
    // try to get date from id
    let dateStr = shortId;
    if (shortId.length >= 8) {
      dateStr = `${shortId.slice(0, 4)}-${shortId.slice(4, 6)}-${shortId.slice(6, 8)}`;
    }
    return `Ảnh Landsat ${dateStr}`;
  };

  // Function to clear all satellite images
  const handleClearAll = () => {
    setS2MosaicItemsLeft([]);
    setS2MosaicItemsRight([]);
    setS2MosaicQuarterlyItemsLeft([]);
    setS2MosaicQuarterlyItemsRight([]);
    setLandsatMosaicItemsLeft([]);
    setLandsatMosaicItemsRight([]);
    setS2SingleItemsLeft([]);
    setS2SingleItemsRight([]);
    setPlanetItemsLeft([]);
    setPlanetItemsRight([]);
    setLandsatItemsLeft([]);
    setLandsatItemsRight([]);
  };



  return (
    <>
      <UI_Wrapper>
        {/* Header with record count and filter button */}
        <UI_Header>
          <Space>
            <Button type="primary" onClick={() => setShowS2MosaicPanel(true)}>
              Tìm kiếm ảnh vệ tinh
            </Button>
          </Space>
        </UI_Header>

        <div>
          <UI_GroupHeader>Bản đồ bên phải {rightCount > 0 ? `(${rightCount})` : ''}</UI_GroupHeader>
          {rightCount === 0 && (
            <UI_EmptyText>Chưa có ảnh nào trên bản đồ phải</UI_EmptyText>
          )}
          {/* S2Mosaic images - right */}
          {s2mosaicItemsRight.map((item) => (
            <SatelliteImageItem
              key={`s2mosaic-right-${item.id}`}
              item={item}
              name={getS2MosaicName(item)}
              imageType="s2mosaic"
              side="right"
              imageVisibility={imageVisibility}
              setImageVisibility={setImageVisibility}
              setS2MosaicItemsLeft={setS2MosaicItemsLeft}
              setS2MosaicItemsRight={setS2MosaicItemsRight}
              setS2MosaicQuarterlyItemsLeft={setS2MosaicQuarterlyItemsLeft}
              setS2MosaicQuarterlyItemsRight={setS2MosaicQuarterlyItemsRight}
              setS2SingleItemsLeft={setS2SingleItemsLeft}
              setS2SingleItemsRight={setS2SingleItemsRight}
              setPlanetItemsLeft={setPlanetItemsLeft}
              setPlanetItemsRight={setPlanetItemsRight}
              setLandsatItemsLeft={setLandsatItemsLeft}
              setLandsatItemsRight={setLandsatItemsRight}
            />
          ))}
          {/* S2MosaicQuarterly images - right */}
          {s2MosaicQuarterlyItemsRight.map((item) => (
            <SatelliteImageItem
              key={`s2mosaic-quarterly-right-${item.id}`}
              item={item}
              name={getS2MosaicQuarterlyName(item)}
              imageType="s2mosaic_quarterly"
              side="right"
              imageVisibility={imageVisibility}
              setImageVisibility={setImageVisibility}
              setS2MosaicItemsLeft={setS2MosaicItemsLeft}
              setS2MosaicItemsRight={setS2MosaicItemsRight}
              setS2MosaicQuarterlyItemsLeft={setS2MosaicQuarterlyItemsLeft}
              setS2MosaicQuarterlyItemsRight={setS2MosaicQuarterlyItemsRight}
              setS2SingleItemsLeft={setS2SingleItemsLeft}
              setS2SingleItemsRight={setS2SingleItemsRight}
              setPlanetItemsLeft={setPlanetItemsLeft}
              setPlanetItemsRight={setPlanetItemsRight}
              setLandsatItemsLeft={setLandsatItemsLeft}
              setLandsatItemsRight={setLandsatItemsRight}
            />
          ))}
          {/* LandsatMosaic images - right */}
          {landsatMosaicItemsRight.map((item) => (
            <SatelliteImageItem
              key={`landsat-mosaic-right-${item.id}`}
              item={item}
              name={getLandsatMosaicName(item)}
              imageType="landsat_mosaic"
              side="right"
              imageVisibility={imageVisibility}
              setImageVisibility={setImageVisibility}
              setS2MosaicItemsLeft={setS2MosaicItemsLeft}
              setS2MosaicItemsRight={setS2MosaicItemsRight}
              setS2MosaicQuarterlyItemsLeft={setS2MosaicQuarterlyItemsLeft}
              setS2MosaicQuarterlyItemsRight={setS2MosaicQuarterlyItemsRight}
              setLandsatMosaicItemsLeft={setLandsatMosaicItemsLeft}
              setLandsatMosaicItemsRight={setLandsatMosaicItemsRight}
              setS2SingleItemsLeft={setS2SingleItemsLeft}
              setS2SingleItemsRight={setS2SingleItemsRight}
              setPlanetItemsLeft={setPlanetItemsLeft}
              setPlanetItemsRight={setPlanetItemsRight}
              setLandsatItemsLeft={setLandsatItemsLeft}
              setLandsatItemsRight={setLandsatItemsRight}
            />
          ))}
          {/* S2Single images - right */}
          {s2singleItemsRight.map((item) => (
            <SatelliteImageItem
              key={`s2single-right-${item.id}`}
              item={item}
              name={getS2SingleName(item)}
              imageType="s2single"
              side="right"
              imageVisibility={imageVisibility}
              setImageVisibility={setImageVisibility}
              setS2MosaicItemsLeft={setS2MosaicItemsLeft}
              setS2MosaicItemsRight={setS2MosaicItemsRight}
              setS2SingleItemsLeft={setS2SingleItemsLeft}
              setS2SingleItemsRight={setS2SingleItemsRight}
              setPlanetItemsLeft={setPlanetItemsLeft}
              setPlanetItemsRight={setPlanetItemsRight}
              setLandsatItemsLeft={setLandsatItemsLeft}
              setLandsatItemsRight={setLandsatItemsRight}
            />
          ))}
          {/* Planet images - right */}
          {planetItemsRight.map((item) => (
            <SatelliteImageItem
              key={`planet-right-${item.id}`}
              item={item}
              name={getPlanetName(item)}
              imageType="planet"
              side="right"
              imageVisibility={imageVisibility}
              setImageVisibility={setImageVisibility}
              setS2MosaicItemsLeft={setS2MosaicItemsLeft}
              setS2MosaicItemsRight={setS2MosaicItemsRight}
              setS2SingleItemsLeft={setS2SingleItemsLeft}
              setS2SingleItemsRight={setS2SingleItemsRight}
              setPlanetItemsLeft={setPlanetItemsLeft}
              setPlanetItemsRight={setPlanetItemsRight}
              setLandsatItemsLeft={setLandsatItemsLeft}
              setLandsatItemsRight={setLandsatItemsRight}
            />
          ))}
          {/* Landsat images - right */}
          {landsatItemsRight.map((item) => (
            <SatelliteImageItem
              key={`landsat-right-${item.id}`}
              item={item}
              name={getLandsatName(item)}
              imageType="landsat"
              side="right"
              imageVisibility={imageVisibility}
              setImageVisibility={setImageVisibility}
              setS2MosaicItemsLeft={setS2MosaicItemsLeft}
              setS2MosaicItemsRight={setS2MosaicItemsRight}
              setS2SingleItemsLeft={setS2SingleItemsLeft}
              setS2SingleItemsRight={setS2SingleItemsRight}
              setPlanetItemsLeft={setPlanetItemsLeft}
              setPlanetItemsRight={setPlanetItemsRight}
              setLandsatItemsLeft={setLandsatItemsLeft}
              setLandsatItemsRight={setLandsatItemsRight}
            />
          ))}
        </div>

        <div>
          <UI_GroupHeader>Bản đồ bên trái (để so sánh) {leftCount > 0 ? `(${leftCount})` : ''}</UI_GroupHeader>
          {leftCount === 0 && (
            <UI_EmptyText>Chưa có ảnh nào trên bản đồ trái</UI_EmptyText>
          )}
          {/* S2Mosaic images - left */}
          {s2mosaicItemsLeft.map((item) => (
            <SatelliteImageItem
              key={`s2mosaic-left-${item.id}`}
              item={item}
              name={getS2MosaicName(item)}
              imageType="s2mosaic"
              side="left"
              imageVisibility={imageVisibility}
              setImageVisibility={setImageVisibility}
              setS2MosaicItemsLeft={setS2MosaicItemsLeft}
              setS2MosaicItemsRight={setS2MosaicItemsRight}
              setS2MosaicQuarterlyItemsLeft={setS2MosaicQuarterlyItemsLeft}
              setS2MosaicQuarterlyItemsRight={setS2MosaicQuarterlyItemsRight}
              setS2SingleItemsLeft={setS2SingleItemsLeft}
              setS2SingleItemsRight={setS2SingleItemsRight}
              setPlanetItemsLeft={setPlanetItemsLeft}
              setPlanetItemsRight={setPlanetItemsRight}
              setLandsatItemsLeft={setLandsatItemsLeft}
              setLandsatItemsRight={setLandsatItemsRight}
            />
          ))}
          {/* S2MosaicQuarterly images - left */}
          {s2MosaicQuarterlyItemsLeft.map((item) => (
            <SatelliteImageItem
              key={`s2mosaic-quarterly-left-${item.id}`}
              item={item}
              name={getS2MosaicQuarterlyName(item)}
              imageType="s2mosaic_quarterly"
              side="left"
              imageVisibility={imageVisibility}
              setImageVisibility={setImageVisibility}
              setS2MosaicItemsLeft={setS2MosaicItemsLeft}
              setS2MosaicItemsRight={setS2MosaicItemsRight}
              setS2MosaicQuarterlyItemsLeft={setS2MosaicQuarterlyItemsLeft}
              setS2MosaicQuarterlyItemsRight={setS2MosaicQuarterlyItemsRight}
              setS2SingleItemsLeft={setS2SingleItemsLeft}
              setS2SingleItemsRight={setS2SingleItemsRight}
              setPlanetItemsLeft={setPlanetItemsLeft}
              setPlanetItemsRight={setPlanetItemsRight}
              setLandsatItemsLeft={setLandsatItemsLeft}
              setLandsatItemsRight={setLandsatItemsRight}
            />
          ))}
          {/* LandsatMosaic images - left */}
          {landsatMosaicItemsLeft.map((item) => (
            <SatelliteImageItem
              key={`landsat-mosaic-left-${item.id}`}
              item={item}
              name={getLandsatMosaicName(item)}
              imageType="landsat_mosaic"
              side="left"
              imageVisibility={imageVisibility}
              setImageVisibility={setImageVisibility}
              setS2MosaicItemsLeft={setS2MosaicItemsLeft}
              setS2MosaicItemsRight={setS2MosaicItemsRight}
              setS2MosaicQuarterlyItemsLeft={setS2MosaicQuarterlyItemsLeft}
              setS2MosaicQuarterlyItemsRight={setS2MosaicQuarterlyItemsRight}
              setLandsatMosaicItemsLeft={setLandsatMosaicItemsLeft}
              setLandsatMosaicItemsRight={setLandsatMosaicItemsRight}
              setS2SingleItemsLeft={setS2SingleItemsLeft}
              setS2SingleItemsRight={setS2SingleItemsRight}
              setPlanetItemsLeft={setPlanetItemsLeft}
              setPlanetItemsRight={setPlanetItemsRight}
              setLandsatItemsLeft={setLandsatItemsLeft}
              setLandsatItemsRight={setLandsatItemsRight}
            />
          ))}
          {/* S2Single images - left */}
          {s2singleItemsLeft.map((item) => (
            <SatelliteImageItem
              key={`s2single-left-${item.id}`}
              item={item}
              name={getS2SingleName(item)}
              imageType="s2single"
              side="left"
              imageVisibility={imageVisibility}
              setImageVisibility={setImageVisibility}
              setS2MosaicItemsLeft={setS2MosaicItemsLeft}
              setS2MosaicItemsRight={setS2MosaicItemsRight}
              setS2SingleItemsLeft={setS2SingleItemsLeft}
              setS2SingleItemsRight={setS2SingleItemsRight}
              setPlanetItemsLeft={setPlanetItemsLeft}
              setPlanetItemsRight={setPlanetItemsRight}
              setLandsatItemsLeft={setLandsatItemsLeft}
              setLandsatItemsRight={setLandsatItemsRight}
            />
          ))}
          {/* Planet images - left */}
          {planetItemsLeft.map((item) => (
            <SatelliteImageItem
              key={`planet-left-${item.id}`}
              item={item}
              name={getPlanetName(item)}
              imageType="planet"
              side="left"
              imageVisibility={imageVisibility}
              setImageVisibility={setImageVisibility}
              setS2MosaicItemsLeft={setS2MosaicItemsLeft}
              setS2MosaicItemsRight={setS2MosaicItemsRight}
              setS2SingleItemsLeft={setS2SingleItemsLeft}
              setS2SingleItemsRight={setS2SingleItemsRight}
              setPlanetItemsLeft={setPlanetItemsLeft}
              setPlanetItemsRight={setPlanetItemsRight}
              setLandsatItemsLeft={setLandsatItemsLeft}
              setLandsatItemsRight={setLandsatItemsRight}
            />
          ))}
          {/* Landsat images - left */}
          {landsatItemsLeft.map((item) => (
            <SatelliteImageItem
              key={`landsat-left-${item.id}`}
              item={item}
              name={getLandsatName(item)}
              imageType="landsat"
              side="left"
              imageVisibility={imageVisibility}
              setImageVisibility={setImageVisibility}
              setS2MosaicItemsLeft={setS2MosaicItemsLeft}
              setS2MosaicItemsRight={setS2MosaicItemsRight}
              setS2SingleItemsLeft={setS2SingleItemsLeft}
              setS2SingleItemsRight={setS2SingleItemsRight}
              setPlanetItemsLeft={setPlanetItemsLeft}
              setPlanetItemsRight={setPlanetItemsRight}
              setLandsatItemsLeft={setLandsatItemsLeft}
              setLandsatItemsRight={setLandsatItemsRight}
            />
          ))}
        </div>
      </UI_Wrapper>

      {/* Main Filter Drawer */}
      <Drawer
        title="Tìm kiếm ảnh vệ tinh"
        placement="right"
        closable={true}
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
        width={375}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px' }}>Loại ảnh vệ tinh</label>
            <Select
              style={{ width: '100%' }}
              value={satelliteType}
              onChange={(value) => setSatelliteType(value)}
              options={[
                { value: SatelliteTypeEnum.S2_MOSAIC, label: 'S2 Ghép ảnh' },
                { value: SatelliteTypeEnum.S2_SINGLE, label: 'S2 Đơn' },
                { value: SatelliteTypeEnum.PLANET, label: 'Planet' },
              ]}
            />
          </div>
          
          <div>
            {satelliteType === SatelliteTypeEnum.S2_MOSAIC && (
              <p>Chọn loại ảnh S2 Ghép ảnh để mở bộ lọc cụ thể</p>
            )}
            {satelliteType === SatelliteTypeEnum.S2_SINGLE && (
              <p>Chọn loại ảnh S2 Đơn để mở bộ lọc cụ thể</p>
            )}
            {satelliteType === SatelliteTypeEnum.PLANET && (
              <p>Chọn loại ảnh Planet để mở bộ lọc cụ thể</p>
            )}
          </div>
          
          <div>
            <Button 
              type="primary" 
              onClick={() => {
                // Close the drawer and open the appropriate finder
                setFilterDrawerVisible(false);
                setShowS2MosaicPanel(true); // For now, always open the S2Mosaic finder
              }}
            >
              Mở tìm kiếm
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

const getThumbnailSrc = (item: any, imageType: string): string => {
  if (!item) return '';

  if (imageType === 's2mosaic') {
    let url = item.thumbUrl;
    if (url) {
      if (url.startsWith('/')) return `${API_URL_ADMIN}${url}`;
      return url.replace('http://localhost:11000', API_URL_ADMIN).replace('http://localhost:12000', API_URL_ADMIN);
    }
    const startDate = item.imageDescription?.start_date || item.start_date;
    const endDate = item.imageDescription?.end_date || item.end_date;
    if (startDate && endDate) {
      return `${API_URL_ADMIN}/api/remote-image-alert-ext/sentinel-2-mosaic/thumbnail?name=${dayjs(startDate).format('YYYY-MM-DD')}_${dayjs(endDate).format('YYYY-MM-DD')}.png`;
    }
  } else if (imageType === 's2mosaic_quarterly') {
    let url = item.thumbUrl;
    if (url) {
      if (url.startsWith('/')) return `${API_URL_ADMIN}${url}`;
      return url.replace('http://localhost:11000', API_URL_ADMIN).replace('http://localhost:12000', API_URL_ADMIN);
    }
    return `${API_URL_ADMIN}/api/remote-image-alert-ext/sentinel-2-mosaic-quarterly/thumbnail?id=${item.id}`;
  } else if (imageType === 'landsat_mosaic') {
    let url = item.thumbUrl;
    if (url) {
      if (url.startsWith('/')) return `${API_URL_ADMIN}${url}`;
      return url.replace('http://localhost:11000', API_URL_ADMIN).replace('http://localhost:12000', API_URL_ADMIN);
    }
    return `${API_URL_ADMIN}/api/remote-image-alert-ext/landsat-mosaic-bimonthly/thumbnail?id=${item.id}`;
  } else if (imageType === 's2single') {
    if (item.thumbUrl) {
      const url = item.thumbUrl;
      if (url.startsWith('/')) return `${API_URL_ADMIN}${url}`;
      return url.replace('http://localhost:11000', API_URL_ADMIN).replace('http://localhost:12000', API_URL_ADMIN);
    }
    if (item.id) {
      return `${API_URL_ADMIN}/api/remote-image-alert-ext/sentinel-2/thumbnail?id=${encodeURIComponent(item.id)}`;
    }
  } else if (imageType === 'landsat') {
    if (item.thumbUrl) {
      const url = item.thumbUrl;
      if (url.startsWith('/')) return `${API_URL_ADMIN}${url}`;
      return url.replace('http://localhost:11000', API_URL_ADMIN).replace('http://localhost:12000', API_URL_ADMIN);
    }
    if (item.id) {
      return `${API_URL_ADMIN}/api/remote-image-alert-ext/landsat/thumbnail?id=${encodeURIComponent(item.id)}`;
    }
  } else if (imageType === 'planet') {
    return item.thumbnail_url || item.thumbUrl || item.url || '';
  }
  return '';
};

interface DisplayInfo {
  tag: { label: string; color: string };
  timeAndArea: string;
  meta: string;
}

const getItemDisplayInfo = (item: any, imageType: string): DisplayInfo => {
  if (imageType === 's2mosaic_quarterly') {
    const startStr = item.start_date ? dayjs(item.start_date).format('DD/MM/YYYY') : '';
    const endStr = item.end_date ? dayjs(item.end_date).format('DD/MM/YYYY') : '';
    const timeStr = startStr && endStr ? `${startStr} - ${endStr}` : `Quý ${item.quarter} / ${item.year}`;
    return {
      tag: { label: 'S2 Quý', color: '#722ed1' },
      timeAndArea: `${item.title_vi || timeStr} • Việt Nam`,
      meta: 'CDSE leastCC • 10m',
    };
  }

  if (imageType === 'landsat_mosaic') {
    const startStr = item.start_date ? dayjs(item.start_date).format('DD/MM/YYYY') : '';
    const endStr = item.end_date ? dayjs(item.end_date).format('DD/MM/YYYY') : '';
    const timeStr = startStr && endStr ? `${startStr} - ${endStr}` : `Kỳ ${item.period} / ${item.year}`;
    return {
      tag: { label: 'Landsat Ghép', color: '#fa8c16' },
      timeAndArea: `${item.title_vi || timeStr} • Việt Nam`,
      meta: 'CDSE ARD • 30m',
    };
  }

  if (imageType === 's2mosaic') {
    const startDate = item.imageDescription?.start_date || item.start_date;
    const endDate = item.imageDescription?.end_date || item.end_date;
    const startStr = startDate ? dayjs(startDate).format('DD/MM/YYYY') : '';
    const endStr = endDate ? dayjs(endDate).format('DD/MM/YYYY') : '';
    const timeStr = startStr && endStr ? `${startStr} - ${endStr}` : startStr || 'Toàn thời gian';
    const areaStr = item.province_name || item.imageDescription?.province || item.province || item.name || 'Toàn quốc';

    return {
      tag: { label: 'S2 Mosaic', color: '#722ed1' },
      timeAndArea: `${timeStr} • ${areaStr}`,
      meta: 'Lọc mây • 10m',
    };
  }

  if (imageType === 's2single') {
    const rawId = item.id || '';
    const id = rawId.split('/').at(-1) || '';

    // Thời gian
    let timeStr = '';
    const datetimePart = id.split('_')[0] || '';
    if (datetimePart.length >= 8) {
      const datePart = datetimePart.slice(0, 8);
      const isoString = `${datePart.slice(0, 4)}-${datePart.slice(4, 6)}-${datePart.slice(6, 8)}`;
      timeStr = dayjs(isoString).format('DD/MM/YYYY');
    } else if (item.created_at) {
      timeStr = dayjs(item.created_at).format('DD/MM/YYYY');
    }

    // Khu vực: Tỉnh thành hoặc Ô MGRS
    let areaStr = item.province_name || item.imageDescription?.province || item.province || '';
    if (!areaStr) {
      const mgrsProp = item.imageDescription?.properties?.MGRS_TILE;
      if (mgrsProp) {
        areaStr = `Ô ${mgrsProp.startsWith('T') ? mgrsProp : `T${mgrsProp}`}`;
      } else {
        const matchMgrs = id.match(/_T([0-9]{2}[A-Z]{3})/);
        if (matchMgrs) {
          areaStr = `Ô T${matchMgrs[1]}`;
        } else {
          const matchCoord = id.match(/_([0-9]+[NSEW]_[0-9]+[NSEW])/);
          if (matchCoord) {
            areaStr = matchCoord[1].replace('_', ' ');
          } else {
            const parts = id.split('_T');
            if (parts.length > 1) {
              areaStr = `Ô T${parts.at(-1)}`;
            }
          }
        }
      }
    }
    if (!areaStr) {
      areaStr = 'Việt Nam';
    }

    // Mây & độ phân giải
    let cloudVal: number | null = null;
    if (item.cloud_percentage !== undefined && item.cloud_percentage !== null) {
      cloudVal = Number(item.cloud_percentage);
    } else if (item.imageDescription?.properties?.CLOUDY_PIXEL_PERCENTAGE !== undefined) {
      cloudVal = Number(item.imageDescription.properties.CLOUDY_PIXEL_PERCENTAGE);
    }

    const metaParts = [];
    if (cloudVal !== null && !isNaN(cloudVal)) {
      metaParts.push(`Mây: ${cloudVal.toFixed(1)}%`);
    }
    metaParts.push('10m');

    return {
      tag: { label: 'Sentinel-2', color: '#1677ff' },
      timeAndArea: timeStr ? `${timeStr} • ${areaStr}` : areaStr,
      meta: metaParts.join(' • '),
    };
  }

  if (imageType === 'landsat') {
    const rawId = item.id || '';

    // Thời gian
    let timeStr = '';
    const dateMatch = rawId.match(/(\d{4})(\d{2})(\d{2})/);
    if (dateMatch) {
      timeStr = `${dateMatch[3]}/${dateMatch[2]}/${dateMatch[1]}`;
    } else if (item.created_at) {
      timeStr = dayjs(item.created_at).format('DD/MM/YYYY');
    }

    // Khu vực: Tỉnh thành hoặc Path/Row
    let areaStr = item.province_name || item.imageDescription?.province || item.province || '';
    if (!areaStr) {
      const wrsPath = item.imageDescription?.properties?.WRS_PATH;
      const wrsRow = item.imageDescription?.properties?.WRS_ROW;
      if (wrsPath && wrsRow) {
        areaStr = `P${wrsPath} R${wrsRow}`;
      } else {
        const pathRowMatch = rawId.match(/_(\d{3})(\d{3})_/);
        if (pathRowMatch) {
          areaStr = `P${pathRowMatch[1]} R${pathRowMatch[2]}`;
        }
      }
    }
    if (!areaStr) {
      areaStr = 'Việt Nam';
    }

    // Mây & độ phân giải
    let cloudVal: number | null = null;
    if (item.cloud_percentage !== undefined && item.cloud_percentage !== null) {
      cloudVal = Number(item.cloud_percentage);
    } else if (item.imageDescription?.properties?.CLOUD_COVER !== undefined) {
      cloudVal = Number(item.imageDescription.properties.CLOUD_COVER);
    }

    const metaParts = [];
    if (cloudVal !== null && !isNaN(cloudVal)) {
      metaParts.push(`Mây: ${cloudVal.toFixed(1)}%`);
    }
    metaParts.push('30m');

    return {
      tag: { label: 'Landsat', color: '#fa8c16' },
      timeAndArea: timeStr ? `${timeStr} • ${areaStr}` : areaStr,
      meta: metaParts.join(' • '),
    };
  }

  if (imageType === 'planet') {
    const timeStr = item.title_vi || item.title || (item.year ? `Năm ${item.year}` : 'Ảnh Planet');
    const areaStr = 'Vùng nhiệt đới';

    return {
      tag: { label: 'Planet', color: '#13c2c2' },
      timeAndArea: `${timeStr} • ${areaStr}`,
      meta: 'NICFI • 4.77m',
    };
  }

  return {
    tag: { label: 'Vệ tinh', color: 'default' },
    timeAndArea: item.id || 'Ảnh vệ tinh',
    meta: '',
  };
};

// Satellite Image List Item Component
interface SatelliteImageItemProps {
  item: any;
  name: string;
  imageType: 's2mosaic' | 's2mosaic_quarterly' | 'landsat_mosaic' | 's2single' | 'planet' | 'landsat';
  side: 'left' | 'right';
  imageVisibility: Record<string, boolean>;
  setImageVisibility: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setS2MosaicItemsLeft: (value: any) => void;
  setS2MosaicItemsRight: (value: any) => void;
  setS2MosaicQuarterlyItemsLeft?: (value: any) => void;
  setS2MosaicQuarterlyItemsRight?: (value: any) => void;
  setLandsatMosaicItemsLeft?: (value: any) => void;
  setLandsatMosaicItemsRight?: (value: any) => void;
  setS2SingleItemsLeft: (value: any) => void;
  setS2SingleItemsRight: (value: any) => void;
  setPlanetItemsLeft: (value: any) => void;
  setPlanetItemsRight: (value: any) => void;
  setLandsatItemsLeft?: (value: any) => void;
  setLandsatItemsRight?: (value: any) => void;
}

const getBoundsFromItem = (item: any): [[number, number], [number, number]] | null => {
  if (!item) return null;
  const bbox = item.bbox;
  if (!bbox) return null;

  if (bbox.coordinates && Array.isArray(bbox.coordinates[0])) {
    const coords = bbox.coordinates[0];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    coords.forEach((pt: any) => {
      const x = Number(pt[0]);
      const y = Number(pt[1]);
      if (!isNaN(x) && !isNaN(y)) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    });
    if (minX !== Infinity && maxX !== -Infinity) {
      return [[minX, minY], [maxX, maxY]];
    }
  }

  if (Array.isArray(bbox) && bbox.length === 4) {
    return [[bbox[0], bbox[1]], [bbox[2], bbox[3]]];
  }

  return null;
};

const getMapLayerId = (imageType: string, side: string, id: string) => {
  if (imageType === 'landsat') {
    return side === 'left' ? `landsat-layer-left-${id}` : `landsat-layer-right-${id}`;
  } else if (imageType === 'landsat_mosaic') {
    return side === 'left' ? `landsat-mosaic-layer-left-${id}` : `landsat-mosaic-layer-right-${id}`;
  } else if (imageType === 's2mosaic') {
    return `s2mosaic-${id}`;
  } else if (imageType === 's2mosaic_quarterly') {
    return side === 'left' ? `s2mosaic-quarterly-layer-left-${id}` : `s2mosaic-quarterly-layer-right-${id}`;
  } else if (imageType === 'planet') {
    return `planet-layer-${id}`;
  } else if (imageType === 's2single') {
    return side === 'left' ? `s2single-layer-left-${id}` : `s2single-layer-right-${id}`;
  }
  return '';
};

const SatelliteImageItem = memo((props: SatelliteImageItemProps) => {
  const {
    item,
    name,
    imageType,
    side,
    imageVisibility,
    setImageVisibility,
    setS2MosaicItemsLeft,
    setS2MosaicItemsRight,
    setS2MosaicQuarterlyItemsLeft,
    setS2MosaicQuarterlyItemsRight,
    setLandsatMosaicItemsLeft,
    setLandsatMosaicItemsRight,
    setS2SingleItemsLeft,
    setS2SingleItemsRight,
    setPlanetItemsLeft,
    setPlanetItemsRight,
    setLandsatItemsLeft,
    setLandsatItemsRight,
  } = props;

  const [imgError, setImgError] = useState(false);
  const itemId = `${imageType}-${side}-${item.id}`;
  const tippyRef = useRef<any>(null);
  const isVisible = imageVisibility[itemId] !== false; // Default to true

  const thumbUrl = getThumbnailSrc(item, imageType);
  const displayInfo = getItemDisplayInfo(item, imageType);

  const handleZoomTo = useCallback(() => {
    if (tippyRef.current) tippyRef.current.hide();
    const bounds = getBoundsFromItem(item);
    const targetMap = side === 'left' ? (window.beforeMap || window.map) : (window.map || window.beforeMap);
    if (bounds && targetMap) {
      targetMap.fitBounds(bounds, { padding: 40 });
    } else {
      console.log('No valid bounds found for item:', item);
    }
  }, [item, side]);

  const handleShowDetails = useCallback(() => {
    if (tippyRef.current) tippyRef.current.hide();
    Modal.info({
      title: 'Chi tiết ảnh vệ tinh',
      width: 520,
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          <div><strong>Thời gian & Khu vực:</strong> {displayInfo.timeAndArea}</div>
          <div><strong>Loại ảnh:</strong> {displayInfo.tag.label} ({imageType.toUpperCase()})</div>
          <div><strong>ID:</strong> {item.id}</div>
          {displayInfo.meta && (
            <div><strong>Thông số:</strong> {displayInfo.meta}</div>
          )}
          {item.cloud_percentage !== undefined && (
            <div><strong>Độ phủ mây:</strong> {Number(item.cloud_percentage).toFixed(1)}%</div>
          )}
          {item.created_at && (
            <div><strong>Ngày tạo:</strong> {dayjs(item.created_at).format('DD/MM/YYYY HH:mm')}</div>
          )}
          {item.wmsUrl && (
            <div style={{ wordBreak: 'break-all', fontSize: 12, color: '#666' }}>
              <strong>Tile URL:</strong> {item.wmsUrl}
            </div>
          )}
        </div>
      ),
    });
  }, [item, displayInfo, imageType]);

  const handleToggleVisibility = useCallback(() => {
    const nextVisible = !isVisible;
    setImageVisibility(prev => ({
      ...prev,
      [itemId]: nextVisible
    }));

    const targetMap = side === 'left' ? window.beforeMap : window.map;
    if (targetMap) {
      const layerId = getMapLayerId(imageType, side, item.id);
      if (layerId && targetMap.getLayer(layerId)) {
        targetMap.setLayoutProperty(layerId, 'visibility', nextVisible ? 'visible' : 'none');
      }
    }
  }, [itemId, isVisible, setImageVisibility, side, imageType, item.id]);

  const handleDownload = useCallback(() => {
    if (tippyRef.current) tippyRef.current.hide();
    if (item.thumbUrl) {
      window.open(item.thumbUrl, '_blank');
    } else if (item.url) {
      window.open(item.url, '_blank');
    } else if (item.wmsUrl) {
      window.open(item.wmsUrl, '_blank');
    }
  }, [item]);

  const handleDelete = useCallback(() => {
    // Remove layer from map
    const targetMap = side === 'left' ? window.beforeMap : window.map;
    if (targetMap) {
      const layerId = getMapLayerId(imageType, side, item.id);
      if (layerId) {
        if (targetMap.getLayer(layerId)) targetMap.removeLayer(layerId);
        if (targetMap.getSource(layerId)) targetMap.removeSource(layerId);
      }
    }

    // Remove item from the appropriate atom
    if (imageType === 's2mosaic') {
      if (side === 'left') {
        setS2MosaicItemsLeft((prev: any[]) => prev.filter((i: any) => i.id !== item.id));
      } else {
        setS2MosaicItemsRight((prev: any[]) => prev.filter((i: any) => i.id !== item.id));
      }
    } else if (imageType === 's2mosaic_quarterly' && setS2MosaicQuarterlyItemsLeft && setS2MosaicQuarterlyItemsRight) {
      if (side === 'left') {
        setS2MosaicQuarterlyItemsLeft((prev: any[]) => prev.filter((i: any) => i.id !== item.id));
      } else {
        setS2MosaicQuarterlyItemsRight((prev: any[]) => prev.filter((i: any) => i.id !== item.id));
      }
    } else if (imageType === 'landsat_mosaic' && setLandsatMosaicItemsLeft && setLandsatMosaicItemsRight) {
      if (side === 'left') {
        setLandsatMosaicItemsLeft((prev: any[]) => prev.filter((i: any) => i.id !== item.id));
      } else {
        setLandsatMosaicItemsRight((prev: any[]) => prev.filter((i: any) => i.id !== item.id));
      }
    } else if (imageType === 's2single') {
      if (side === 'left') {
        setS2SingleItemsLeft((prev: any[]) => prev.filter((i: any) => i.id !== item.id));
      } else {
        setS2SingleItemsRight((prev: any[]) => prev.filter((i: any) => i.id !== item.id));
      }
    } else if (imageType === 'planet') {
      if (side === 'left') {
        setPlanetItemsLeft((prev: any[]) => prev.filter((i: any) => i.id !== item.id));
      } else {
        setPlanetItemsRight((prev: any[]) => prev.filter((i: any) => i.id !== item.id));
      }
    } else if (imageType === 'landsat' && setLandsatItemsLeft && setLandsatItemsRight) {
      if (side === 'left') {
        setLandsatItemsLeft((prev: any[]) => prev.filter((i: any) => i.id !== item.id));
      } else {
        setLandsatItemsRight((prev: any[]) => prev.filter((i: any) => i.id !== item.id));
      }
    }
    // Remove visibility state
    setImageVisibility(prev => {
      const newState = { ...prev };
      delete newState[itemId];
      return newState;
    });
  }, [item, imageType, side, itemId, setImageVisibility, setS2MosaicItemsLeft, setS2MosaicItemsRight, setS2MosaicQuarterlyItemsLeft, setS2MosaicQuarterlyItemsRight, setLandsatMosaicItemsLeft, setLandsatMosaicItemsRight, setS2SingleItemsLeft, setS2SingleItemsRight, setPlanetItemsLeft, setPlanetItemsRight, setLandsatItemsLeft, setLandsatItemsRight]);

  return (
    <UI_ListItem $hidden={!isVisible}>
      <UI_ThumbnailWrapper>
        {thumbUrl && !imgError ? (
          <UI_ThumbnailImg
            src={thumbUrl}
            alt={displayInfo.timeAndArea}
            onError={() => setImgError(true)}
            loading="lazy"
          />
        ) : (
          <UI_ThumbnailFallback>
            <PictureOutlined style={{ fontSize: 18, color: '#8c8c8c' }} />
          </UI_ThumbnailFallback>
        )}
      </UI_ThumbnailWrapper>

      <UI_ListItemContent>
        <UI_ListItemTitle title={displayInfo.timeAndArea}>
          {displayInfo.timeAndArea}
        </UI_ListItemTitle>
        <UI_ListItemRow2>
          <Tag
            color={displayInfo.tag.color}
            style={{
              margin: 0,
              fontSize: 10,
              lineHeight: '16px',
              padding: '0 4px',
              borderRadius: 3,
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            {displayInfo.tag.label}
          </Tag>
          {displayInfo.meta && (
            <UI_ListItemSubtitle title={displayInfo.meta}>
              {displayInfo.meta}
            </UI_ListItemSubtitle>
          )}
        </UI_ListItemRow2>
      </UI_ListItemContent>

      <UI_ListItemControls>
        {/* Context Menu */}
        <Tippy
          trigger="click"
          interactive={true}
          placement="bottom"
          offset={[0, 10]}
          delay={0}
          render={(attrs) => (
            <UI_Options_Wrapper>
              {/* Phóng tới */}
              <UI_Options_Button onClick={handleZoomTo}>
                <UI_Options_ButtonIcon>
                  <PiMagnifyingGlass />
                </UI_Options_ButtonIcon>
                <span>Phóng tới</span>
              </UI_Options_Button>

              {/* Chi tiết */}
              <UI_Options_Button onClick={handleShowDetails}>
                <UI_Options_ButtonIcon>
                  <PiInfo />
                </UI_Options_ButtonIcon>
                <span>Chi tiết</span>
              </UI_Options_Button>

              {/* Tải xuống */}
              <UI_Options_Button onClick={handleDownload}>
                <UI_Options_ButtonIcon>
                  <PiDownloadSimple />
                </UI_Options_ButtonIcon>
                <span>Tải xuống</span>
              </UI_Options_Button>

              {/* Xóa */}
              <Popconfirm
                placement="right"
                title="Xoá ảnh này khỏi bản đồ"
                description="Bạn có chắc chắn muốn xoá ảnh này?"
                onConfirm={handleDelete}
                okText="Xoá"
                cancelText="Huỷ"
              >
                <UI_Options_Button>
                  <UI_Options_ButtonIcon>
                    <PiTrash />
                  </UI_Options_ButtonIcon>
                  <span>Xóa</span>
                </UI_Options_Button>
              </Popconfirm>
            </UI_Options_Wrapper>
          )}
          onCreate={(instance) => { tippyRef.current = instance; }}
        >
          <Button type="text" style={{ padding: 4, minWidth: 28, height: 28 }}>
            <IoEllipsisHorizontal />
          </Button>
        </Tippy>

        {/* Visibility Toggle */}
        <Button 
          type="text" 
          style={{ padding: 4, minWidth: 28, height: 28 }} 
          onClick={handleToggleVisibility}
          title={isVisible ? 'Ẩn lớp ảnh' : 'Hiện lớp ảnh'}
        >
          {isVisible ? <PiEye /> : <PiEyeSlash style={{ color: 'gray' }} />}
        </Button>
      </UI_ListItemControls>
    </UI_ListItem>
  );
});

export default memo(SatelliteTab);

const UI_Wrapper = styled.div`
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UI_Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
`;

const UI_ListItem = styled.div<{ $hidden?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  background-color: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  margin-bottom: 6px;
  gap: 8px;
  transition: all 0.2s ease;
  opacity: ${props => (props.$hidden ? 0.5 : 1)};

  &:hover {
    background-color: #f5f5f5;
    border-color: #d9d9d9;
  }
`;

const UI_ThumbnailWrapper = styled.div`
  width: 44px;
  height: 44px;
  min-width: 44px;
  max-width: 44px;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f0f0f0;
  border: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const UI_ThumbnailImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const UI_ThumbnailFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
`;

const UI_ListItemContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const UI_ListItemRow2 = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const UI_ListItemTitle = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: #1f1f1f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.35;
`;

const UI_ListItemSubtitle = styled.span`
  font-size: 11px;
  color: #8c8c8c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const UI_EmptyText = styled.div`
  padding: 12px;
  text-align: center;
  color: #bfbfbf;
  font-size: 12px;
  font-style: italic;
  background-color: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  margin-bottom: 8px;
`;

const UI_ListItemControls = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const UI_Options_Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  padding: 5px 0;
  min-width: 150px;
  z-index: 1000;
`;

const UI_Options_Button = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: 0.3s;
  font-size: 14px;
  &:hover {
    background-color: var(--color-primary);
    color: white;
  }
  &.active {
    background-color: var(--color-primary);
    color: white;
  }
`;

const UI_Options_ButtonIcon = styled.div`
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;